import { makeAutoObservable, runInAction } from 'mobx'
import { Simulate } from 'react-dom/test-utils'
import { InferenceImageStore } from 'store/inference/InferenceImageStore'
import { InferenceVideoStore } from 'store/inference/InferenceVideoStore'
import { opt } from 'store/opt'
import { openInferenceChannel } from 'store/socket/channel/inference'
import { InferenceEvent } from 'store/socket/listener'
import { store } from 'store/store'
import { Gender, IdentityMode } from 'type/Identity'
import {
  DISLIKE,
  Inference,
  InferenceResponse,
  InferenceStatus,
  ItemData,
  LIKE,
  LikeMode,
  ShowoffTags,
  TagData,
} from 'type/Inference'
import { parseDate } from 'util/date'
import { getNextKey } from 'util/id'
import { notNil } from 'util/null'

export type NewInferenceData = {
  identity_id: string | undefined
  identity_mode: IdentityMode
  pic: string | undefined
  selected_item_images: string[]
  item: ItemData
  combo: ItemData | undefined
  source: InferenceStore | undefined
}

export class InferenceStore {
  readonly key = getNextKey()
  empty = true

  inference_id: string = ''
  identity_id?: string
  gender?: Gender
  pic?: string

  item_id?: string
  collection_id?: string
  product?: string
  article?: string
  brand?: string
  color?: string
  links?: string
  links_name?: string
  clothing_sizes?: string

  created_at!: number
  status!: InferenceStatus
  private _viewed!: boolean
  is_video!: boolean
  like_mode!: LikeMode

  items: ItemData[] = []
  images: InferenceImageStore[] = []
  videos: InferenceVideoStore[] = []

  selected_item_images: string[] = []

  private _showoff_tags?: ShowoffTags
  private _image_element: HTMLImageElement | undefined

  static fromInference(json: Inference) {
    const store = new InferenceStore()
    store.applyJson(json)
    store.empty = false
    void store.updating()
    return store
  }

  static fromEmpty() {
    return new InferenceStore()
  }

  private constructor() {
    makeAutoObservable(this)
  }

  onError() {
    this.status = 'error'
  }

  get complete() {
    return this.status === 'complete'
  }

  get error() {
    return this.status === 'error'
  }

  get sources(): string[] {
    return this.images.map(i => i.src)
  }

  get source(): string | undefined {
    return this.sources[0] || undefined
  }

  get video(): string | undefined {
    return this.videos[0]?.src || undefined
  }

  get url(): string {
    const { inference_id } = this
    return `${opt.share_host}/tryon/${inference_id}/image`
  }

  get image_id(): number | undefined {
    return this.videos[0]?.id ?? this.images[0]?.id
  }

  onInference(json: Inference) {
    this.applyJson(json)
  }

  get image_element(): HTMLImageElement | undefined {
    return this._image_element
  }

  set image_element(value: HTMLImageElement | undefined) {
    this._image_element = value
  }

  onView() {
    this.viewed = true
  }

  toggleLike(like: boolean) {
    const like_mode = like ? LIKE : DISLIKE
    const reset = this.like_mode === like_mode
    this.like_mode = reset ? 0 : like_mode
  }

  get viewed(): boolean {
    return this._viewed
  }

  set viewed(value: boolean) {
    if (this._viewed && !value) return
    this._viewed = value
  }

  private async updating() {
    if (this.complete || this.empty) return
    openInferenceChannel(this.inference_id,
      event => this.onEvent(event),
      json => this.onInference(json))
  }

  private onEvent(event: InferenceEvent) {
    this.status = event.status

    const image = event.images?.[0]
    if (!image) return

    if (!event.images?.length) return

    if (event.status === 'complete') {
      void store.inferences.loadInner(false).then(() => {
        runInAction(() => {
          const updatedInference = store.inferences.inferences.find(it => it.inference_id === this.inference_id)
          if (updatedInference && updatedInference !== this) {
            this.images = updatedInference.images
            this.videos = updatedInference.videos
            this.status = updatedInference.status
            this.viewed = updatedInference.viewed
            this.like_mode = updatedInference.like_mode
            this.is_video = updatedInference.is_video
            this.created_at = updatedInference.created_at
            this._showoff_tags = updatedInference.showoff_tags
            this.items = updatedInference.items
            this.item_id = updatedInference.item_id
            this.collection_id = updatedInference.collection_id
            this.product = updatedInference.product
            this.article = updatedInference.article
            this.brand = updatedInference.brand
            this.color = updatedInference.color
            this.links = updatedInference.links
            this.links_name = updatedInference.links_name
            this.clothing_sizes = updatedInference.clothing_sizes
          }
        })
      })
    }
  }

  get showoff_tags(): ShowoffTags | undefined {
    return this._showoff_tags
  }

  private applyJson(json: Inference) {
    const { inference_id, status, is_viewed, generated } = json
    const image = json.generated?.[0]
    const created = parseDate(image?.created_at)?.getTime() ?? 0
    const mode = image?.like_mode
    const tags = image?.showoff_tags?.data

    this.inference_id = inference_id
    this._showoff_tags = image?.showoff_tags
    this.applyTagData(tags)

    // Дата есть только у готовой картинки. Пока её нет, держим уже известную,
    // а для впервые увиденной генерации считаем датой текущий момент: она идёт
    // прямо сейчас, и с нулём она улетала бы в конец списка.
    this.created_at = created || this.created_at || Date.now()
    this.status = status
    // У одного образа апстрим не отдаёт is_viewed - в этом случае держим известное значение.
    if (is_viewed != null) this.viewed = !!is_viewed
    this.is_video = !!image?.is_video
    this.like_mode = mode ?? 0

    this.images = generated?.map(InferenceImageStore.fromJson) ?? []
    this.videos = generated?.map(InferenceVideoStore.fromJson).filter(notNil) ?? []
  }

  private applyTagData(tag: TagData | undefined) {
    const identity = tag?.identity
    const data = tag?.custom_collection_data

    this.identity_id = tag?.identity_id
    this.gender = identity?.gender
    this.pic = identity?.face1_url || identity?.user_url || undefined

    this.item_id = data?.item
    this.collection_id = data?.collection
    this.product = data?.product
    this.article = data?.article
    this.color = data?.color
    this.brand = data?.brand
    this.links = data?.links
    this.links_name = data?.links_name
    this.clothing_sizes = data?.clothing_sizes

    if (tag?.items?.length) {
      this.items = tag.items
    } else if (data) {
      this.items = [data]
    }
  }

  beforeCreate(data: NewInferenceData) {
    const { item, combo, source } = data

    this.identity_id = data.identity_id
    this.pic = data.pic || undefined

    this.selected_item_images = data.selected_item_images

    this.item_id = item.item
    this.collection_id = item.collection
    this.product = item.product
    this.article = item.article
    this.color = item.color
    this.brand = item.brand
    this.links = item.links
    this.links_name = item.links_name

    this.created_at = Date.now()
    this.status = 'unknown'
    this.viewed = false
    this.is_video = !!source
    this.like_mode = 0

    this.items = [item, combo].filter(notNil)
    this.images = source?.images ?? []
    this.videos = []
  }

  afterCreate(response: InferenceResponse) {
    const { inference_id, status } = response
    this.inference_id = inference_id
    this.status = status
    this.empty = false
    void this.updating()
  }
}
